import { useState, useEffect } from 'react';
import { Search, Star, X, CheckCircle } from 'lucide-react';
import { Button } from '../../_shared/components';
import type { ScorecardTemplate } from './types';

interface TemplateSidebarProps {
  templates: ScorecardTemplate[];
  /** Called when user clicks a template row — shows preview, does not apply yet */
  onPreview: (t: ScorecardTemplate | null) => void;
  /** Called when user confirms Apply — actually replaces the waterfall */
  onApply: (t: ScorecardTemplate) => void;
  onClose: () => void;
}

function TemplateRow({ t, selected, onSelect, showCampaignName }: {
  t: ScorecardTemplate;
  selected: ScorecardTemplate | null;
  onSelect: (t: ScorecardTemplate) => void;
  showCampaignName?: boolean;
}) {
  const isSelected = selected?.id === t.id;
  return (
    <button
      key={t.id}
      onClick={() => onSelect(t)}
      className={`w-full text-left px-4 py-3 transition-colors border-b border-nightshade-100 dark:border-nightshade-800 last:border-0 ${
        isSelected
          ? 'bg-lemon-50 dark:bg-lemon-950/30 border-l-2 border-l-lemon-400 dark:border-l-lemon-600'
          : 'hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isSelected ? 'text-lemon-800 dark:text-lemon-300' : 'text-nightshade-900 dark:text-nightshade-100'}`}>
            {t.name}
          </p>
          {showCampaignName && t.campaign_name && (
            <p className="text-xs text-nightshade-400 mt-0.5 truncate">{t.campaign_name}</p>
          )}
          {!showCampaignName && t.campaign_subtype && (
            <p className="text-xs text-nightshade-400 mt-0.5">{t.campaign_subtype}</p>
          )}
          {t.created_at && (
            <p className="text-xs text-nightshade-300 dark:text-nightshade-600 mt-0.5">
              {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
        {isSelected
          ? <CheckCircle className="w-4 h-4 text-lemon-500 dark:text-lemon-400 flex-shrink-0 mt-0.5" />
          : t.is_default && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blueberry-600 dark:text-blueberry-400">
                <Star className="w-3 h-3" /> Default
              </span>
            )
        }
      </div>
    </button>
  );
}

export function TemplateSidebar({ templates, onPreview, onApply, onClose }: TemplateSidebarProps) {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ScorecardTemplate | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleClose = () => {
    onPreview(null);
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleSelect = (t: ScorecardTemplate) => {
    setSelected(t);
    onPreview(t);
  };

  const handleDiscard = () => {
    setSelected(null);
    onPreview(null);
  };

  const handleApply = () => {
    if (!selected) return;
    onApply(selected);
  };

  const filtered = templates.filter(t => !query || t.name.toLowerCase().includes(query.toLowerCase()) || (t.campaign_name ?? '').toLowerCase().includes(query.toLowerCase()));
  const sortByDate = (a: typeof templates[0], b: typeof templates[0]) => {
    if (a.created_at && b.created_at) return b.created_at.localeCompare(a.created_at);
    if (a.created_at) return -1;
    if (b.created_at) return 1;
    return a.name.localeCompare(b.name);
  };
  const regularTemplates = filtered.filter(t => t.source !== 'launch').sort(sortByDate);

  return (
    <div className={`flex-shrink-0 h-full overflow-hidden transition-[width] duration-200 ${visible ? 'w-80' : 'w-0'}`}>
      <div className="w-80 h-full flex flex-col bg-white dark:bg-nightshade-950 border-l border-nightshade-200 dark:border-nightshade-800 shadow-xl">

        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          <span className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50 flex-1">Load template</span>
          <button
            onClick={handleClose}
            className="p-1 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nightshade-400" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search templates…"
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
            />
          </div>
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto">
          {regularTemplates.length === 0 ? (
            <p className="px-4 py-6 text-xs text-nightshade-400 italic text-center">No templates match.</p>
          ) : (
            regularTemplates.map(t => <TemplateRow key={t.id} t={t} selected={selected} onSelect={handleSelect} />)
          )}
        </div>

        {/* Footer — only visible when a template is selected */}
        {selected && (
          <div className="flex-shrink-0 border-t border-nightshade-200 dark:border-nightshade-700 bg-lemon-50 dark:bg-lemon-950/20 px-4 py-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-lemon-800 dark:text-lemon-300 truncate">{selected.name}</p>
              <p className="text-xs text-nightshade-500 mt-0.5">Previewing above — your current rows are unchanged.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleDiscard} className="flex-1">
                Discard
              </Button>
              <Button variant="primary" size="sm" onClick={handleApply} className="flex-1">
                Apply template
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
